/**
 * @description Renders an individual personnel mapping field that maps an excel header to a SECCON field
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import styled from '@mui/system/styled';
import Popper from '@mui/material/Popper';

const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiAutocomplete-groupLabel': {
    backgroundColor: theme.palette.grey['200']
  }
}));

/**
 * @prop availableFields {string[]} - a list of SECCON fields that the excel header could be mapped to
 * @prop header {string} - the display name of the excel header to be mapped
 * @prop id {string} - the id of the mapping field
 * @prop selections {{ [field]: boolean }} - a list of field selections that have already been made. Its used to
 *  disable the select options that have already been selected elsewhere in the form.
 * @prop onSelection {Function} - a callback function for when a selection has been amde
 */
const MappingField = ({
  availableFields,
  header,
  id,
  selections,
  onSelection
}) => {
  const { control } = useFormContext();

  return (
    <Controller
      control={control}
      name={`fields.${ id }`}
      render={({ field: { onChange, value } }) => {
        /**
         * Handles change events by calling the form's onChange events and the parent component's onSelection callback
         */
        function handleChange (selection) {
          onChange(selection);
          onSelection(value, selection);
        }

        return (
          <FormControl fullWidth>
            <Autocomplete
              autoHighlight
              getOptionDisabled={(option) => selections[option.value] && option.value !== 'clearance.indoctrinationLevel' && option.value !== 'personnelSpecialAccess.name'}
              getOptionLabel={(option) => option.label}
              groupBy={(option) => option.category}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              options={availableFields}
              onChange={(_, selection) => handleChange(selection)}
              renderInput={(params) => (
                <TextField
                  // Autocomplete provides the list of params and passes it to TextField; both are third party libraries out of our control
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label={header}
                />
              )}
              PopperComponent={StyledPopper}
              value={value ?? null}
            />
          </FormControl>
        );
      }}
    />
  );
}

MappingField.propTypes = {
  availableFields: PropTypes.arrayOf(PropTypes.object).isRequired,
  header: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  selections: PropTypes.object.isRequired,
  onSelection: PropTypes.func.isRequired
};

export default MappingField;
